
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { LandingPage } from "@/components/landing/LandingPage";
import { useEffect } from "react";
import { logAuthState } from "@/utils/debug-auth";

const Index = () => {
  // Get auth state from context - loading state is handled by parent components
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  
  console.log('[Index] Rendering based on auth state, user exists:', !!user);
  if (user) {
    console.log('[Index] User metadata:', user.user_metadata);
    console.log('[Index] App metadata:', user.app_metadata);
  }
  console.log('[Index] Return to parameter:', returnTo);
  
  // Debug auth state on page load
  useEffect(() => {
    logAuthState();
  }, []);
  
  // Check for pending test invites and restore context after OAuth
  useEffect(() => {
    const ssToken = sessionStorage.getItem('pendingTestInviteToken');
    const ssTestId = sessionStorage.getItem('pendingTestId');
    const ssRedirectFlag = sessionStorage.getItem('redirectToJobPage');

    const lsToken = localStorage.getItem('pendingTestInviteToken');
    const lsTestId = localStorage.getItem('pendingTestId');
    const lsRedirectFlag = localStorage.getItem('redirectToJobPage');
    const lsPostAuthReturnTo = localStorage.getItem('postAuthReturnTo');

    console.log('[Index] Restoring pending context if needed:', {
      ssToken, ssTestId, ssRedirectFlag, lsToken, lsTestId, lsRedirectFlag, lsPostAuthReturnTo
    });

    // Restore from localStorage if sessionStorage is missing (common after OAuth redirects)
    if (!ssToken && lsToken) sessionStorage.setItem('pendingTestInviteToken', lsToken);
    if (!ssTestId && lsTestId) sessionStorage.setItem('pendingTestId', lsTestId);
    if (!ssRedirectFlag && lsRedirectFlag) sessionStorage.setItem('redirectToJobPage', lsRedirectFlag);
  }, [user]);
  
  // If user is authenticated, decide redirect target with strong priority rules
  if (user) {
    const pendingToken = sessionStorage.getItem('pendingTestInviteToken') || localStorage.getItem('pendingTestInviteToken');
    const postAuthReturnTo = localStorage.getItem('postAuthReturnTo');
    const effectiveReturn = postAuthReturnTo || returnTo || (pendingToken ? `/test/invite/${pendingToken}` : undefined);

    // If role is missing, send user to role selection with preserved returnTo
    if (user.user_metadata?.role === undefined) {
      const target = effectiveReturn || '/';
      console.log('[Index] User is missing role, redirecting to role selection with returnTo:', target);
      return <Navigate to={`/role-selection${target ? `?returnTo=${encodeURIComponent(target)}` : ''}`} replace />;
    }

    // If we have a post-auth target, consume it first
    if (postAuthReturnTo) {
      console.log('[Index] Redirecting to postAuthReturnTo:', postAuthReturnTo);
      try { localStorage.removeItem('postAuthReturnTo'); } catch {}
      return <Navigate to={postAuthReturnTo} replace />;
    }

    // Handle pending test invite with "consume once" logic
    if (pendingToken) {
      // Check if we've already redirected to this token in this session
      const consumedToken = sessionStorage.getItem('consumedInviteToken');
      
      if (consumedToken === pendingToken) {
        // Already redirected once, clear the tokens to allow normal navigation
        console.log('[Index] Already redirected to this invite, clearing tokens');
        sessionStorage.removeItem('pendingTestInviteToken');
        sessionStorage.removeItem('consumedInviteToken');
        try {
          localStorage.removeItem('pendingTestInviteToken');
          localStorage.removeItem('pendingTestId');
          localStorage.removeItem('redirectToJobPage');
        } catch {}
        // Don't redirect again, fall through to dashboard
      } else {
        // First redirect to this token, mark it as consumed
        console.log('[Index] Redirecting to pending test invite:', pendingToken);
        sessionStorage.setItem('consumedInviteToken', pendingToken);
        return <Navigate to={`/test/invite/${pendingToken}`} replace />;
      }
    }

    // Then honor explicit returnTo param
    if (returnTo) {
      console.log('[Index] Redirecting to:', returnTo);
      return <Navigate to={returnTo} replace />;
    }

    // Fallback
    console.log('[Index] Redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  // Otherwise show landing page for unauthenticated users
  return <LandingPage />;
};

export default Index;
